<!-- Patient_Task.vue — Task detail within Patient Drawer -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 flex flex-col max-h-full overflow-hidden">
    <!-- Loading -->
    <div v-if="loading" class="p-5 space-y-4">
      <div class="flex items-center gap-2">
        <div class="w-6 h-6 bg-gray-100 rounded animate-pulse"></div>
        <div>
          <div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-3 w-20 bg-gray-100 rounded animate-pulse mt-1"></div>
        </div>
      </div>
      <div class="pt-3 border-t border-gray-100">
        <div class="h-3 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
          <div v-for="n in 3" :key="'sk-det-' + n" class="contents">
            <div class="h-3 w-14 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [80, 100, 70][n - 1] + 'px' }"></div>
          </div>
        </div>
      </div>
      <div class="pt-3 border-t border-gray-100">
        <div class="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="space-y-2">
          <div v-for="n in 2" :key="'sk-log-' + n" class="flex items-center gap-3 px-3 py-2.5 rounded bg-gray-50">
            <div class="w-7 h-7 rounded bg-gray-200 animate-pulse shrink-0"></div>
            <div class="space-y-1 flex-1">
              <div class="h-3 w-28 bg-gray-200 rounded animate-pulse"></div>
              <div class="h-2.5 w-36 bg-gray-100 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="pt-3 border-t border-gray-100">
        <div class="h-3 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="h-3 bg-gray-100 rounded animate-pulse w-full"></div>
        <div class="h-3 bg-gray-100 rounded animate-pulse w-3/4 mt-1"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-8 px-5">
      <p class="text-xs text-red-600">{{ error }}</p>
      <button @click="fetchTask" class="mt-2 text-xs text-gray-500 hover:text-gray-700 underline">Retry</button>
    </div>

    <!-- Task content -->
    <template v-else-if="task">
      <!-- Scrollable content -->
      <div class="flex-1 overflow-y-auto p-5">
      <!-- Back + Title -->
      <div class="flex items-center gap-2 mb-4">
        <button
          @click="$emit('close')"
          class="p-1 -ml-1 text-gray-400 hover:text-gray-600 transition-colors rounded hover:bg-gray-100"
          title="Back to programs"
        >
          <FontAwesomeIcon :icon="['fas', 'arrow-left']" class="text-xs" />
        </button>
        <div>
          <div class="text-sm font-semibold text-gray-900">{{ task.task_type_name }}</div>
          <div class="flex items-center gap-2 mt-0.5">
            <span class="text-xs text-gray-500">{{ programName }}</span>
            <span v-if="task.status === TaskStatus.SNOOZED" class="px-1.5 py-px text-xs font-medium rounded bg-yellow-100 text-yellow-700">Snoozed</span>
            <span v-if="isOverdue" class="px-1.5 py-px text-xs font-medium rounded bg-red-100 text-red-700">Overdue</span>
          </div>
        </div>
      </div>

      <!-- Snooze alert -->
      <div v-if="task.status === TaskStatus.SNOOZED" class="mb-4 p-2.5 bg-amber-50 border border-amber-200 rounded flex items-center justify-between">
        <span class="text-xs text-amber-800">
          Snoozed until <span class="font-medium">{{ formatDate(task.metadata?.snooze_until) }}</span>
        </span>
        <button @click="triggerUnsnooze" :disabled="unsnoozing" class="text-xs font-medium text-amber-700 hover:text-amber-900">
          {{ unsnoozing ? 'Unsnoozing...' : 'Unsnooze' }}
        </button>
      </div>

      <!-- Instructions -->
      <div v-if="task.metadata?.custom_instructions" class="mb-4 p-2.5 bg-blue-50 border border-blue-200 rounded">
        <p class="text-xs text-blue-900 whitespace-pre-wrap">{{ task.metadata.custom_instructions }}</p>
      </div>

      <!-- Task Details -->
      <div class="pt-3 border-t border-gray-100 mb-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Task Details</div>
        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <span class="text-xs text-gray-500">Status</span>
          <span class="text-xs text-gray-900">{{ taskStatusLabel }}</span>
          <template v-if="task.due_date">
            <span class="text-xs text-gray-500">Due</span>
            <span :class="['text-xs', isOverdue ? 'text-red-600 font-medium' : 'text-gray-900']">{{ formatDate(task.due_date) }}<template v-if="task.due_time"> at {{ formatDueTime(task.due_time, task.due_date) }}</template></span>
          </template>
          <template v-if="task.activated_at">
            <span class="text-xs text-gray-500">Activated</span>
            <span class="text-xs text-gray-900">{{ formatDate(task.activated_at) }}</span>
          </template>
        </div>
      </div>

      <Patient_Task_AIRecommendation v-if="aiRecommendation" :metadata="aiRecommendation" :patient-identifier="aiRecommendationPatient" />

      <!-- Log Work -->
      <div class="pt-3 border-t border-gray-100 mb-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Quick Log Work</div>
        <WorkLogger
          :task="task"
          :opportunities="task.opportunities"
          :task-id="task.id"
          :task-type-id="task.task_type"
          :task-metadata="task.metadata"
          @work-submitted="onWorkSubmitted"
        />
        <!-- Clinical Engagement -->
        <button
          v-if="hasOpportunity"
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-50 transition-colors disabled:opacity-60"
          :disabled="startingClinicalEngagement"
          @click="openClinicalEngagement"
        >
          <div class="w-7 h-7 rounded flex items-center justify-center shrink-0" :class="engagements.clinicalEngagement.tileClass">
            <FontAwesomeIcon :icon="engagements.clinicalEngagement.icon" class="text-xs" :class="engagements.clinicalEngagement.iconClass" />
          </div>
          <div>
            <span class="text-xs font-medium text-gray-900">{{ startingClinicalEngagement ? 'Starting...' : engagements.clinicalEngagement.label }}</span>
            <p class="text-xs text-gray-400">{{ engagements.clinicalEngagement.description }}</p>
          </div>
        </button>
        <p v-if="clinicalEngagementError" class="px-3 text-xs text-red-600">{{ clinicalEngagementError }}</p>

        <!-- Other engagement types configured to launch from an opportunity (e.g. Outreach Engagement) -->
        <template v-if="hasOpportunity">
          <button
            v-for="engagement in taskEngagements"
            :key="engagement.workTypeId"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-50 transition-colors disabled:opacity-60"
            :disabled="startingEngagementId === engagement.workTypeId"
            @click="openEngagement(engagement)"
          >
            <div class="w-7 h-7 rounded flex items-center justify-center shrink-0 bg-amber-50">
              <FontAwesomeIcon :icon="engagement.icon" class="text-xs text-amber-600" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-900">{{ startingEngagementId === engagement.workTypeId ? 'Starting...' : engagement.label }}</span>
              <p class="text-xs text-gray-400">{{ engagement.description }}</p>
            </div>
          </button>
          <p v-if="engagementError" class="px-3 text-xs text-red-600">{{ engagementError }}</p>
        </template>

        <template v-if="showPhoneCallLauncher">
          <button
            v-if="!confirmingPhoneCall"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-50 transition-colors"
            @click="confirmingPhoneCall = true"
          >
            <div class="w-7 h-7 rounded flex items-center justify-center shrink-0" :class="engagements.phoneCall.tileClass">
              <FontAwesomeIcon :icon="engagements.phoneCall.icon" class="text-xs" :class="engagements.phoneCall.iconClass" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-900">{{ engagements.phoneCall.label }}</span>
              <p class="text-xs text-gray-400">{{ engagements.phoneCall.description }}</p>
            </div>
          </button>
          <div v-else class="mt-1 rounded-lg border border-green-200 bg-green-50/60 p-3" data-test="phone-call-confirm">
            <div class="flex items-start gap-3">
              <div class="w-7 h-7 rounded flex items-center justify-center shrink-0" :class="engagements.phoneCall.tileClass">
                <FontAwesomeIcon :icon="engagements.phoneCall.icon" class="text-xs" :class="engagements.phoneCall.iconClass" />
              </div>
              <div class="min-w-0">
                <p class="text-xs font-semibold text-gray-900">Start a {{ engagements.phoneCall.label }}?</p>
                <p class="mt-0.5 text-xs text-gray-600">This creates a call record on this task and opens the call page.</p>
              </div>
            </div>
            <p class="mt-3 flex items-start gap-1.5 rounded border border-yellow-200 bg-yellow-50 px-2.5 py-2 text-xs text-yellow-800" data-test="phone-call-patient-caution">
              <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mt-0.5 text-yellow-500" />
              <span>Calling the patient? Use a {{ engagements.clinicalEngagement.label }} instead.</span>
            </p>
            <p v-if="phoneCallError" class="mt-2 text-xs text-red-600">{{ phoneCallError }}</p>
            <div class="mt-3 flex justify-end gap-2">
              <button class="px-2.5 py-1 text-xs text-gray-600 border border-gray-300 rounded bg-white hover:bg-gray-50" :disabled="startingPhoneCall" @click="cancelPhoneCall">Cancel</button>
              <button
                class="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300"
                :disabled="startingPhoneCall"
                @click="launchPhoneCall"
              >
                <FontAwesomeIcon :icon="engagements.phoneCall.icon" class="mr-1" />{{ startingPhoneCall ? 'Starting...' : 'Start call' }}
              </button>
            </div>
          </div>
        </template>

        <!-- Schedule Telehealth -->
        <div v-if="hasOpportunity">
          <!-- Scheduled state -->
          <div v-if="scheduledSession" class="mt-1 px-3 py-2.5 bg-gray-50 rounded">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-medium text-gray-900">Telehealth Scheduled</span>
              <span v-if="scheduledSession.scheduled_at" class="text-xs text-gray-500">{{ formatScheduleDisplay(scheduledSession.scheduled_at) }}</span>
            </div>
            <div class="flex items-center gap-1.5">
              <input
                :value="scheduledSession.patient_join_url"
                readonly
                class="flex-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600 truncate"
              />
              <button @click="copyScheduledLink" class="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded hover:bg-white transition-colors" title="Copy link">
                Copy
              </button>
              <button
                @click="emailScheduledLink"
                :disabled="scheduledEmailSending"
                class="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded hover:bg-white transition-colors"
                title="Email link to patient"
              >
                {{ scheduledEmailSentTo ? 'Sent' : (scheduledEmailSending ? '...' : 'Email') }}
              </button>
            </div>
          </div>
          <!-- Schedule form -->
          <div v-else>
            <div v-if="showScheduleForm" class="mt-1 px-3 py-2.5 bg-gray-50 rounded">
              <label class="block text-xs text-gray-600 mb-1">Schedule for</label>
              <input
                v-model="scheduledAt"
                type="datetime-local"
                class="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                :min="minScheduleDate"
              />
              <div class="flex items-center gap-2 mt-2">
                <button
                  @click="scheduleTelehealth"
                  :disabled="scheduling || !scheduledAt"
                  class="px-3 py-1.5 bg-gray-900 text-white rounded text-xs font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {{ scheduling ? 'Scheduling...' : 'Confirm' }}
                </button>
                <button
                  @click="showScheduleForm = false; scheduledAt = ''"
                  class="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
            <button
              v-else
              @click="showScheduleForm = true"
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-50 transition-colors"
            >
              <div class="w-7 h-7 rounded bg-neutral-50 flex items-center justify-center shrink-0">
                <FontAwesomeIcon :icon="['fas', 'calendar-check']" class="text-xs text-neutral-600" />
              </div>
              <div>
                <span class="text-xs font-medium text-gray-900">Schedule Telehealth</span>
                <p class="text-xs text-gray-400">Create a session and share the join link</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Activity History -->
      <div class="pt-3 border-t border-gray-100 mb-4">
        <div class="flex items-center justify-between mb-1.5">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Activity</div>
          <button
            @click="$refs.workTimeline?.fetchWorkItems()"
            class="text-gray-400 hover:text-primary-600 transition-colors p-1"
            title="Refresh activity"
          >
            <FontAwesomeIcon :icon="['fas', 'rotate']" class="text-xs" />
          </button>
        </div>
        <Work_Timeline
          ref="workTimeline"
          :task-id="taskId"
          :task-type-id="task.task_type"
          :task-type-name="task.task_type_name"
        />
      </div>

      <!-- Notes + Action -->
      <div class="pt-3 border-t border-gray-100 mb-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Notes & Action</div>

        <!-- Previous notes -->
        <div v-if="previousNotes.length" class="space-y-1.5 mb-3">
          <div v-for="(note, idx) in previousNotes" :key="idx" class="bg-gray-50 rounded p-2.5">
            <p class="text-xs text-gray-700">{{ note.data }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ note.appuser }} · {{ note.created_at }}</p>
          </div>
        </div>

        <textarea
          v-model="additionalNotes"
          class="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-y"
          placeholder="Add notes..."
          rows="3"
        ></textarea>

      </div>

      <!-- History -->
      <div v-if="task.task_prior_actions && task.task_prior_actions.length" class="pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">History ({{ task.task_prior_actions.length }})</div>
        <Task_Actions_List :task-prior-actions="task.task_prior_actions" />
      </div>
      </div><!-- end scrollable -->

      <!-- Pinned action bar -->
      <div class="shrink-0 border-t border-gray-200 bg-gray-50 px-5 py-3">
        <div class="flex items-center gap-2">
          <select
            v-model="selectedAction"
            class="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option disabled value="">Select action...</option>
            <option v-for="action in activeActions" :key="action.id" :value="action">
              {{ action.name }}
            </option>
          </select>
          <button
            @click="submit"
            :disabled="!selectedAction || submitting"
            class="px-3 py-1.5 bg-gray-900 text-white rounded text-xs font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {{ submitting ? 'Submitting...' : 'Submit' }}
          </button>
        </div>

        <!-- Date picker for Snooze (6), Complete (1), Repeat (9), and Update Due Date (16) actions -->
        <div v-if="selectedAction && [...NEEDS_DATE, InstructionType.COMPLETE_SUCCESS, InstructionType.REPEAT].includes(selectedAction.task_instruction_type_id)" class="mt-2 bg-white rounded p-2.5 border border-gray-200">
          <div class="text-[11px] text-gray-500 mb-1">
            {{ selectedAction.task_instruction_type_id === InstructionType.SNOOZE ? 'Snooze until' : selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE ? 'New due date' : 'Schedule next task' }}
            <span v-if="[InstructionType.COMPLETE_SUCCESS, InstructionType.REPEAT].includes(selectedAction.task_instruction_type_id)" class="text-gray-400">(optional — defaults to {{ selectedAction.config?.default_due_days || selectedAction.config?.repeat_strategy?.interval_days || 7 }} days)</span>
          </div>
          <div class="flex items-center gap-1.5 mb-1.5">
            <button
              v-for="opt in snoozePresets"
              :key="opt.days"
              @click="setSnooze(opt.days); userPickedDate = true"
              class="px-2 py-1 rounded text-[11px] font-medium transition-colors"
              :class="snoozeDaysSelected === opt.days
                ? 'bg-gray-900 text-white'
                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'"
            >{{ opt.label }}</button>
          </div>
          <TaskCalendarPicker v-model="snoozeUntil" @update:model-value="snoozeDaysSelected = null; userPickedDate = true" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Task_Actions_List from '@/components/Portal/Task_Tables/Task_Actions_List.vue';
import Patient_Task_AIRecommendation from './Patient_Task_AIRecommendation.vue';
import WorkLogger from '@/components/Portal/Task_Tables/Work_Widgets/WorkLogger.vue';
import Work_Timeline from '@/components/Portal/Task_Tables/Work_Widgets/Work_Timeline.vue';
import TaskCalendarPicker from '@/components/shared/TaskCalendarPicker.vue';
import { formatDate, formatDueTime } from '@/utils/common';
import { submitAction } from '@/services/api/tasks';
import { InstructionType, TaskStatus, NEEDS_DATE } from '@/constants/instructionTypes';
import { blockSensitiveEgress } from '@/utils/demoEgress';
import { parseTelehealthError } from '@/services/api/telehealthErrors';
import {
  ENGAGEMENTS,
  canStartPhoneCall,
  clinicalEngagementPath,
  engagementsForMetrics,
  fetchLaunchableEngagements,
  startClinicalEngagement,
  startEngagement,
  startPhoneCall,
} from '@/utils/engagementLaunch';

export default {
  name: 'Patient_Task',
  components: { FontAwesomeIcon, Task_Actions_List, WorkLogger, Work_Timeline, TaskCalendarPicker, Patient_Task_AIRecommendation },
  props: {
    taskId: { type: [String, Number], required: true }
  },
  emits: ['close', 'submit', 'update_row', 'show-message'],

  data() {
    return {
      InstructionType,
      TaskStatus,
      NEEDS_DATE,
      loading: false,
      error: null,
      task: null,
      selectedAction: '',
      additionalNotes: '',
      snoozeUntil: '',
      snoozeDaysSelected: null,
      // True only after the coordinator clicks a preset or picks a date, never for
      // the pre-filled default. Sent as due_date_source: 'user' so Complete/Repeat
      // honour the date instead of their due_days_map / interval (same rule as
      // ClinicalEngagement.vue; see instructions.py).
      userPickedDate: false,
      snoozePresets: [
        { label: '1 day', days: 1 },
        { label: '3 days', days: 3 },
        { label: '1 week', days: 7 },
      ],
      unsnoozing: false,
      submitting: false,
      scheduling: false,
      scheduledSession: null,
      scheduledEmailSending: false,
      scheduledEmailSentTo: null,
      showScheduleForm: false,
      scheduledAt: '',
      engagements: ENGAGEMENTS,
      confirmingPhoneCall: false,
      startingClinicalEngagement: false,
      clinicalEngagementError: null,
      launchableEngagements: [],
      startingEngagementId: null,
      engagementError: null,
      startingPhoneCall: false,
      phoneCallError: null
    };
  },

  computed: {
    ...mapGetters('auth', ['current_user']),

    taskStatusLabel() {
      const labels = { 0: 'Unassigned', 1: 'Active', 2: 'Closed', 3: 'Complete', 4: 'Snoozed', 5: 'Skipped', 6: 'Expired' };
      return labels[this.task?.status] || 'Unknown';
    },
    programName() {
      if (!this.task?.opportunities?.length) return '';
      return this.task.opportunities[0]?.metric_name || '';
    },

    hasOpportunity() {
      return this.task?.opportunities?.length > 0;
    },

    // Configured engagements (e.g. Outreach) whose WorkAssignment metric matches one of the task's opportunities.
    taskEngagements() {
      return engagementsForMetrics(this.launchableEngagements, (this.task?.opportunities || []).map(o => o.metric));
    },

    aiRecommendationOpportunity() {
      return (this.task?.opportunities || []).find((o) => o.metadata?.ai_final_disposition) || null;
    },

    aiRecommendation() {
      return this.aiRecommendationOpportunity?.metadata || null;
    },

    aiRecommendationPatient() {
      return this.aiRecommendationOpportunity?.patient_identifier || null;
    },

    showPhoneCallLauncher() {
      return !!this.task?.id && canStartPhoneCall();
    },

    isOverdue() {
      if (!this.task || this.task.status === TaskStatus.SNOOZED) return false;
      const d = this.task.due_date;
      if (!d) return false;
      const dueLocal = new Date(d + 'T23:59:59');
      return dueLocal < new Date();
    },

    previousNotes() {
      return this.task?.metadata?.previous_task_completed_notes?.filter(n => n.data) || [];
    },

    activeActions() {
      if (!this.task?.available_action_types) return [];
      // Terminal task — no actions available
      if (![TaskStatus.ACTIVE, TaskStatus.SNOOZED].includes(this.task.status)) return [];
      return this.task.available_action_types.filter(a => {
        if (a.active === false) return false;
        if (a.task_instruction_type_id === InstructionType.UNSNOOZE && this.task.status !== TaskStatus.SNOOZED) return false;
        if (a.task_instruction_type_id === InstructionType.SNOOZE && this.task.status === TaskStatus.SNOOZED) return false;
        return true;
      });
    },

    minScheduleDate() {
      const d = new Date();
      return d.toISOString().slice(0, 16);
    },

    minSnoozeDate() {
      const d = new Date();
      d.setHours(d.getHours() + 1);
      return d.toISOString().slice(0, 16);
    }
  },

  watch: {
    taskId: {
      immediate: true,
      handler(val) { if (val) this.fetchTask(); }
    },
    selectedAction(action) {
      this.snoozeDaysSelected = null;
      this.userPickedDate = false;
      if (action?.task_instruction_type_id === InstructionType.COMPLETE_SUCCESS) {
        const defaultDays = action.config?.default_due_days;
        if (defaultDays) this.setSnooze(defaultDays);
        else this.snoozeUntil = '';
      } else if (action?.task_instruction_type_id === InstructionType.SNOOZE) {
        const defaultDays = action.config?.default_snooze_days || action.config?.snooze_days;
        if (defaultDays) this.setSnooze(defaultDays);
      } else if (action?.task_instruction_type_id === InstructionType.REPEAT) {
        const defaultDays = action.config?.repeat_strategy?.interval_days;
        if (defaultDays) this.setSnooze(defaultDays);
      } else if (action?.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE) {
        const defaultDays = action.config?.default_push_days;
        if (defaultDays) this.setSnooze(defaultDays);
      } else {
        this.snoozeUntil = '';
      }
    }
  },

  mounted() {
    this.loadLaunchableEngagements();
    this._onVisible = () => {
      if (document.visibilityState === 'visible' && this.taskId) {
        this.fetchTask();
        this.$refs.workTimeline?.fetchWorkItems();
      }
    };
    this._onMessage = (event) => {
      if (['clinical-session-saved', 'phone-call-saved'].includes(event.data?.type) && this.taskId) {
        this.fetchTask();
        this.$refs.workTimeline?.fetchWorkItems();
      }
    };
    document.addEventListener('visibilitychange', this._onVisible);
    window.addEventListener('message', this._onMessage);
  },

  beforeUnmount() {
    document.removeEventListener('visibilitychange', this._onVisible);
    window.removeEventListener('message', this._onMessage);
  },

  methods: {
    formatDate,
    formatDueTime,

    setSnooze(days) {
      const d = new Date();
      d.setDate(d.getDate() + days);
      d.setHours(9, 0, 0, 0);
      this.snoozeUntil = d.toISOString().slice(0, 16);
      this.snoozeDaysSelected = days;
    },

    async fetchTask() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`api/tasks/${this.taskId}/`);
        this.task = data;
        this.selectedAction = '';
        this.additionalNotes = '';
        this.snoozeUntil = '';
      } catch (e) {
        this.error = e.response?.data?.message || 'Failed to load task';
      } finally {
        this.loading = false;
      }
    },

    async triggerUnsnooze() {
      const action = this.activeActions.find(a => a.task_instruction_type_id === InstructionType.UNSNOOZE);
      if (!action) return;
      this.unsnoozing = true;
      try {
        await submitAction({
          task_id: this.taskId,
          task_action_type_id: action.id,
          metadata: {}
        });
        this.$emit('update_row');
        this.fetchTask();
      } finally {
        this.unsnoozing = false;
      }
    },

    formatScheduleDisplay(val) {
      try {
        return new Date(val).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      } catch { return val; }
    },

    onWorkSubmitted() {
      this.fetchTask();
      this.$refs.workTimeline?.fetchWorkItems();
    },

    async openClinicalEngagement() {
      if (this.scheduledSession?.work_id) {
        window.open(clinicalEngagementPath(this.scheduledSession.work_id), '_blank');
        return;
      }
      this.startingClinicalEngagement = true;
      this.clinicalEngagementError = null;
      try {
        await startClinicalEngagement({ opportunityId: this.task.opportunities[0]?.id, taskId: this.task.id });
        this.$refs.workTimeline?.fetchWorkItems();
      } catch (error) {
        this.clinicalEngagementError = parseTelehealthError(error, 'Failed to start clinical engagement. Please try again.').summary;
      } finally {
        this.startingClinicalEngagement = false;
      }
    },

    async loadLaunchableEngagements() {
      try {
        this.launchableEngagements = await fetchLaunchableEngagements();
      } catch (error) {
        console.error('Failed to load engagement types', error);
        this.launchableEngagements = [];
      }
    },

    async openEngagement(engagement) {
      this.startingEngagementId = engagement.workTypeId;
      this.engagementError = null;
      try {
        await startEngagement({ workTypeId: engagement.workTypeId, opportunityId: this.task.opportunities[0]?.id, taskId: this.task.id });
        this.$refs.workTimeline?.fetchWorkItems();
      } catch (error) {
        this.engagementError = parseTelehealthError(error, `Failed to start ${engagement.label}. Please try again.`).summary;
      } finally {
        this.startingEngagementId = null;
      }
    },

    cancelPhoneCall() {
      this.confirmingPhoneCall = false;
      this.phoneCallError = null;
    },

    async launchPhoneCall() {
      if (!this.task?.id) return;
      this.startingPhoneCall = true;
      this.phoneCallError = null;
      try {
        await startPhoneCall({ opportunityId: this.task.opportunities?.[0]?.id, taskId: this.task.id });
        this.confirmingPhoneCall = false;
        this.$refs.workTimeline?.fetchWorkItems();
      } catch (error) {
        this.phoneCallError = parseTelehealthError(error, 'Failed to start care coordination call. Please try again.').summary;
      } finally {
        this.startingPhoneCall = false;
      }
    },

    async scheduleTelehealth() {
      const oppId = this.task.opportunities[0]?.id;
      if (!oppId) return;
      this.scheduling = true;
      try {
        const { data } = await axios.post('/api/work/video/create-session/', {
          opportunity_id: oppId,
          task_id: this.taskId,
          scheduled_at: new Date(this.scheduledAt).toISOString(),
        });
        this.scheduledSession = {
          work_id: data.work_id,
          patient_join_url: data.patient_join_url,
          scheduled_at: this.scheduledAt,
        };
        this.showScheduleForm = false;
        this.$root.$emit('show-success-toast', 'Session created — share link or email patient');
        this.$refs.workTimeline?.fetchWorkItems();
      } catch (e) {
        this.$root.$emit('show-error-toast', 'Failed to schedule telehealth');
      } finally {
        this.scheduling = false;
      }
    },

    copyScheduledLink() {
      if (this.scheduledSession?.patient_join_url) {
        if (blockSensitiveEgress('copy')) return;
        navigator.clipboard.writeText(this.scheduledSession.patient_join_url)
          .then(() => this.$root.$emit('show-success-toast', 'Link copied'))
          .catch(() => this.$root.$emit('show-error-toast', 'Failed to copy'));
      }
    },

    async emailScheduledLink() {
      if (this.scheduledEmailSending || this.scheduledEmailSentTo) return;
      this.scheduledEmailSending = true;
      try {
        const { data } = await axios.post(`/api/work/${this.scheduledSession.work_id}/video/send-invite/`);
        if (data.sent) {
          this.scheduledEmailSentTo = data.email;
          this.$root.$emit('show-success-toast', `Invite sent to ${data.email}`);
        } else {
          this.$root.$emit('show-error-toast', data.reason || 'No email on file');
        }
      } catch {
        this.$root.$emit('show-error-toast', 'Failed to send invite');
      } finally {
        this.scheduledEmailSending = false;
      }
    },

    async submit() {
      if (!this.selectedAction) return;

      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE && !this.snoozeUntil) {
        this.$emit('show-message', { text: 'Select a snooze date', type: 'error' });
        return;
      }
      if (this.selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE && !this.snoozeUntil) {
        this.$emit('show-message', { text: 'Select a new due date', type: 'error' });
        return;
      }

      this.submitting = true;
      const metadata = this.additionalNotes ? { additionalData: this.additionalNotes } : {};
      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE) {
        metadata.snooze_until = new Date(this.snoozeUntil).toISOString();
      }
      if (this.selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE && this.snoozeUntil) {
        metadata.due_date = new Date(this.snoozeUntil).toISOString();
      }
      if (this.selectedAction.task_instruction_type_id === InstructionType.REPEAT && this.snoozeUntil) {
        metadata.due_date = new Date(this.snoozeUntil).toISOString();
      }
      if (this.selectedAction.task_instruction_type_id === InstructionType.COMPLETE_SUCCESS && this.snoozeUntil) {
        metadata.due_date = new Date(this.snoozeUntil).toISOString();
      }
      if (metadata.due_date && this.userPickedDate) {
        metadata.due_date_source = 'user';
      }

      try {
        const result = await submitAction({
          task_id: this.taskId,
          task_action_type_id: this.selectedAction.id,
          metadata
        });
        if (result.already_completed) {
          this.$emit('show-message', { text: 'Task was already completed', type: 'warning' });
        }
        this.$emit('submit');
      } catch (e) {
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message || 'Unknown error';
        this.$emit('show-message', { text: `Failed to submit action: ${detail}`, type: 'error' });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
